'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '@/components/common/Header';
import Breadcrumb from '@/components/common/Breadcrumb';
import Icon from '@/components/ui/AppIcon';
import FilterToolbar from './FilterToolbar';
import ListingsTable from './ListingsTable';
import BulkActionBar from './BulkActionBar';
import AuthBlock from './AuthBlock';
import ExportButton from './ExportButton';

export interface Listing {
  id: string;
  title: string;
  status: 'active' | 'inactive' | 'pending' | 'sold' | 'draft';
  category: string;
  price: number;
  condition: string;
  location: string;
  owner_email: string;
  updated_at: string;
  description: string;
}

const VALID_KEY = 'ADMIN2026';

export default function DashboardInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [authKey, setAuthKey] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkConfirm, setBulkConfirm] = useState<{ action: string } | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setIsHydrated(true); }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('admin_authorized');
      if (saved === 'true') setIsAuthorized(true);
    }
  }, []);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (conditionFilter) params.set('condition', conditionFilter);
      if (search) params.set('search', search);
      const res = await fetch(`/api/admin/listings?${params.toString()}`);
      if (!res.ok) throw new Error(`Failed to fetch listings (${res.status})`);
      const json = await res.json();
      setListings(json.listings || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, conditionFilter, search]);

  useEffect(() => {
    if (isAuthorized) fetchListings();
  }, [isAuthorized, fetchListings]);

  const handleAuth = useCallback(() => {
    if (authKey === VALID_KEY) {
      setIsAuthorized(true);
      setAuthError('');
      if (typeof window !== 'undefined') {
        localStorage.setItem('admin_authorized', 'true');
      }
    } else {
      setAuthError(`Not authorised. Use admin key: ${VALID_KEY}`);
    }
  }, [authKey]);

  const filtered = useMemo(() => {
    let result = [...listings];
    if (sortBy === 'newest') result.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    else if (sortBy === 'oldest') result.sort((a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime());
    else if (sortBy === 'price_asc') result.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price_desc') result.sort((a, b) => b.price - a.price);
    return result;
  }, [listings, sortBy]);

  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedIds(checked ? filtered.map(l => l.id) : []);
  }, [filtered]);

  const handleSelectRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
  }, []);

  const handleBulkAction = useCallback((action: string) => {
    setBulkConfirm({ action });
  }, []);

  const confirmBulkAction = useCallback(async () => {
    if (!bulkConfirm) return;
    try {
      const isCategoryAction = bulkConfirm.action.startsWith('category:');
      const category = isCategoryAction ? bulkConfirm.action.replace('category:', '') : null;

      if (bulkConfirm.action === 'delete') {
        await Promise.all(
          selectedIds.map(id =>
            fetch(`/api/admin/listings/${id}`, { method: 'DELETE' })
          )
        );
      } else if (isCategoryAction && category) {
        await Promise.all(
          selectedIds.map(id =>
            fetch(`/api/admin/listings/${id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ category }),
            })
          )
        );
      } else {
        await Promise.all(
          selectedIds.map(id =>
            fetch(`/api/admin/listings/${id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: bulkConfirm.action }),
            })
          )
        );
      }
    } catch (err: any) {
      console.error('Bulk action error:', err.message);
    }
    setSelectedIds([]);
    setBulkConfirm(null);
    fetchListings();
  }, [bulkConfirm, selectedIds, fetchListings]);

  if (!isHydrated) return null;

  if (!isAuthorized) {
    return (
      <AuthBlock
        authKey={authKey}
        authError={authError}
        onKeyChange={setAuthKey}
        onSubmit={handleAuth}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />
      <main className="px-4 md:px-6 py-4">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <Breadcrumb />
            <h1 className="text-lg font-bold text-[var(--color-foreground)] mt-1">Listings Management</h1>
            <p className="text-xs text-[var(--color-muted-foreground)]">{filtered.length} of {listings.length} listings</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton listings={filtered} />
            <a
              href="/comprehensive-listing-editor-panel"
              className="flex items-center gap-1.5 px-3 py-2 bg-[var(--color-primary)] text-[var(--color-primary-foreground)] rounded text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Icon name="PlusIcon" size={16} variant="solid" />
              New Listing
            </a>
          </div>
        </div>

        <FilterToolbar
          search={search}
          statusFilter={statusFilter}
          categoryFilter={categoryFilter}
          conditionFilter={conditionFilter}
          sortBy={sortBy}
          onSearchChange={setSearch}
          onStatusChange={setStatusFilter}
          onCategoryChange={setCategoryFilter}
          onConditionChange={setConditionFilter}
          onSortChange={setSortBy}
        />

        {selectedIds.length > 0 && (
          <BulkActionBar
            count={selectedIds.length}
            onAction={handleBulkAction}
            onClear={() => setSelectedIds([])}
          />
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Icon name="ArrowPathIcon" size={24} variant="outline" className="animate-spin text-[var(--color-muted-foreground)]" />
            <span className="ml-2 text-sm text-[var(--color-muted-foreground)]">Loading listings...</span>
          </div>
        )}

        {error && !loading && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
            <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-red-500 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={fetchListings} className="ml-auto text-xs text-red-600 underline">Retry</button>
          </div>
        )}

        {!loading && !error && (
          <ListingsTable
            listings={filtered}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectRow={handleSelectRow}
          />
        )}

        {bulkConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-[var(--color-card)] rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
              <h3 className="font-bold text-[var(--color-foreground)] mb-2">Confirm Bulk Action</h3>
              <p className="text-sm text-[var(--color-muted-foreground)] mb-4">
                {bulkConfirm.action.startsWith('category:')
                  ? <>Set category to <strong>&quot;{bulkConfirm.action.replace('category:', '')}&quot;</strong> for {selectedIds.length} selected listing(s)?</>
                  : <>Apply &quot;<strong>{bulkConfirm.action}</strong>&quot; to {selectedIds.length} selected listing(s)?</>
                }
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setBulkConfirm(null)} className="px-3 py-1.5 text-sm border border-[var(--color-border)] rounded hover:bg-[var(--color-muted)] transition-colors">Cancel</button>
                <button onClick={confirmBulkAction} className="px-3 py-1.5 text-sm bg-[var(--color-destructive)] text-white rounded hover:opacity-90 transition-opacity">Confirm</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}